import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { PageContainer, PageHeader, EmptyState } from "@/components/Page";
import { StatusBadge, CategoryBadge, CATEGORIES, STATUSES } from "@/components/Badges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

export default function ManagerTickets() {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [residents, setResidents] = useState([]);
  const [managers, setManagers] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", category: "Maintenance", on_behalf_of_resident_id: "", assigned_manager_id: "" });

  const load = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (categoryFilter) params.category = categoryFilter;
      const [t, r, m] = await Promise.all([
        api.get("/tickets", { params }),
        api.get("/users", { params: { role: "resident" } }),
        api.get("/users", { params: { role: "estate_manager" } }),
      ]);
      setTickets(t.data); setResidents(r.data); setManagers(m.data);
    } catch (err) { toast.error(formatApiError(err)); }
  }, [statusFilter, categoryFilter]);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const body = { ...form };
      if (!body.on_behalf_of_resident_id) delete body.on_behalf_of_resident_id;
      if (!body.assigned_manager_id) delete body.assigned_manager_id;
      await api.post("/tickets", body);
      toast.success("Ticket raised");
      setOpen(false);
      setForm({ title: "", description: "", category: "Maintenance", on_behalf_of_resident_id: "", assigned_manager_id: "" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <AppShell>
      <PageContainer>
        <PageHeader
          eyebrow="Estate Manager"
          title="Tickets"
          description="All tickets for your estate. Raise a ticket to another manager or on behalf of a resident."
          actions={
            <Button
              onClick={() => setOpen(true)}
              data-testid="create-ticket-button"
              className="rounded-none bg-black hover:bg-zinc-800 h-10 px-4 uppercase tracking-wider text-xs font-semibold"
            >
              <Plus className="h-4 w-4" /> New Ticket
            </Button>
          }
        />

        <div className="grid grid-cols-2 md:flex md:flex-wrap gap-3 mb-6">
          <div className="w-full sm:w-48">
            <Label className="label-eyebrow">Status</Label>
            <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="rounded-none border-zinc-300 mt-2" data-testid="filter-status"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="w-full sm:w-56">
            <Label className="label-eyebrow">Category</Label>
            <Select value={categoryFilter || "all"} onValueChange={(v) => setCategoryFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="rounded-none border-zinc-300 mt-2" data-testid="filter-category"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {tickets.length === 0 ? (
          <EmptyState title="No tickets" description="No tickets match your filters." />
        ) : (
          <div className="bg-white border border-zinc-200 overflow-x-auto">
            <table className="w-full text-sm min-w-[860px]">
              <thead className="border-b border-zinc-200 bg-zinc-50">
                <tr className="text-left">
                  <th className="p-4 label-eyebrow font-semibold">Title</th>
                  <th className="p-4 label-eyebrow font-semibold">Category</th>
                  <th className="p-4 label-eyebrow font-semibold">Status</th>
                  <th className="p-4 label-eyebrow font-semibold">Raised By</th>
                  <th className="p-4 label-eyebrow font-semibold">Assigned To</th>
                  <th className="p-4 label-eyebrow font-semibold">Created</th>
                </tr>
              </thead>
              <tbody>
                {tickets.map((t) => (
                  <tr key={t.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`ticket-row-${t.id}`}>
                    <td className="p-4">
                      <Link to={`/tickets/${t.id}`} className="font-semibold hover:underline underline-offset-4">{t.title}</Link>
                    </td>
                    <td className="p-4"><CategoryBadge category={t.category} /></td>
                    <td className="p-4"><StatusBadge status={t.status} /></td>
                    <td className="p-4 text-zinc-600 text-xs">{t.raised_by?.name || "—"}</td>
                    <td className="p-4 text-zinc-600 text-xs">{t.assigned_manager?.name || "—"}</td>
                    <td className="p-4 text-zinc-500 text-xs">{new Date(t.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="rounded-none border-zinc-300 max-w-lg" data-testid="ticket-form-dialog">
            <DialogHeader><DialogTitle className="font-display text-2xl font-black tracking-tight">New Ticket</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label className="label-eyebrow">Title</Label>
                <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="ticket-title-input" className="rounded-none border-zinc-300 focus-visible:ring-0 focus-visible:border-black mt-2" />
              </div>
              <div>
                <Label className="label-eyebrow">Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="rounded-none border-zinc-300 mt-2" data-testid="ticket-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-eyebrow">Description</Label>
                <Textarea required rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="ticket-description-input" className="rounded-none border-zinc-300 focus-visible:ring-0 focus-visible:border-black mt-2" />
              </div>
              <div>
                <Label className="label-eyebrow">On behalf of resident (optional)</Label>
                <Select value={form.on_behalf_of_resident_id || "none"} onValueChange={(v) => setForm({ ...form, on_behalf_of_resident_id: v === "none" ? "" : v })}>
                  <SelectTrigger className="rounded-none border-zinc-300 mt-2" data-testid="ticket-onbehalf-select"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {residents.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-eyebrow">Assign to manager (optional)</Label>
                <Select value={form.assigned_manager_id || "none"} onValueChange={(v) => setForm({ ...form, assigned_manager_id: v === "none" ? "" : v })}>
                  <SelectTrigger className="rounded-none border-zinc-300 mt-2" data-testid="ticket-assignto-select"><SelectValue placeholder="Auto-assign to estate manager" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Auto (this estate) —</SelectItem>
                    {managers.filter((m) => m.id !== user?.id).map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button type="submit" data-testid="submit-ticket-button" className="rounded-none bg-black hover:bg-zinc-800 uppercase tracking-wider text-xs font-semibold">Raise Ticket</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </PageContainer>
    </AppShell>
  );
}
